import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, Music2, Radio, Play, Pause } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../contexts/PlayerContext";

const TYPE_BADGE = {
    news: { l: "Actu", c: "#ffc83d", icon: Newspaper },
    track: { l: "Nouveau son", c: "#00d97e", icon: Music2 },
};

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 3600) return `il y a ${Math.max(1, Math.floor(s / 60))} min`;
    if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
    return `il y a ${Math.floor(s / 86400)} j`;
}

/** Aperçu du fil d'actu sur la page d'accueil — 4 dernières publications. */
export default function FeedPreview() {
    const [items, setItems] = useState([]);
    const { current, playing, play, toggle } = usePlayer();

    const handlePlay = async (e, it) => {
        e.preventDefault();
        e.stopPropagation();
        if (current?.id === it.track_id) { toggle(); return; }
        try {
            const { data } = await api.get(`/tracks/${it.track_id}`);
            play(data);
        } catch { /* track indisponible */ }
    };

    useEffect(() => {
        api.get("/feed?limit=30")
            .then(({ data }) => {
                const all = (data.items || []).filter((it) => it.type === "news" || it.type === "track");
                // Mix garanti : jusqu'à 2 derniers sons + le reste en actus (max 4)
                const tracks = all.filter((it) => it.type === "track").slice(0, 2);
                const news = all.filter((it) => it.type === "news");
                const mixed = [...tracks, ...news].slice(0, 4)
                    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                setItems(mixed);
            })
            .catch(() => {});
    }, []);

    if (items.length === 0) return null;

    return (
        <section className="border-t border-[#1c1c1c] bg-[#0a0a0a]" data-testid="feed-preview-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
                    <div>
                        <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#00d97e]">
                            <Radio className="w-3.5 h-3.5" /> En direct de la communauté
                        </p>
                        <h2 className="font-display text-2xl md:text-4xl font-semibold tracking-tight mt-2">Ce qui bouge en ce moment</h2>
                    </div>
                    <Link
                        to="/feed"
                        className="group inline-flex items-center gap-2 border border-[#00d97e]/40 hover:bg-[#00d97e] hover:text-[#050505] text-[#00d97e] font-semibold px-6 py-3 rounded-full transition-colors text-sm"
                        data-testid="feed-preview-see-all"
                    >
                        Voir tout le fil d&apos;actu <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {items.map((it) => {
                        const badge = TYPE_BADGE[it.type] || TYPE_BADGE.news;
                        const BIcon = badge.icon;
                        const artistName = it.artist?.name || "Artiste";
                        const to = it.type === "track" && it.track_id
                            ? `/track/${it.slug || it.track_id}`
                            : `/post/${it.slug || it.id}`;
                        const img = it.image_url || it.cover_url;
                        return (
                            <Link
                                key={`${it.type}-${it.id}`}
                                to={to}
                                className="group relative bg-[#111111] border border-[#1c1c1c] hover:border-[#00d97e]/40 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                                data-testid={`feed-preview-item-${it.id}`}
                            >
                                {it.type === "track" && it.track_id && (
                                    <button
                                        onClick={(e) => handlePlay(e, it)}
                                        className="absolute top-2.5 right-2.5 z-10 w-10 h-10 rounded-full bg-[#00d97e] text-[#050505] flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                                        title="Écouter un extrait"
                                        data-testid={`feed-preview-play-${it.id}`}
                                    >
                                        {current?.id === it.track_id && playing
                                            ? <Pause className="w-4 h-4" fill="currentColor" />
                                            : <Play className="w-4 h-4 ml-0.5" fill="currentColor" />}
                                    </button>
                                )}
                                {img && (
                                    <div className="h-32 overflow-hidden bg-[#161616]">
                                        <img decoding="async" src={img} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                )}
                                <div className="p-4">
                                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full" style={{ background: `${badge.c}1A`, color: badge.c }}>
                                        <BIcon className="w-3 h-3" /> {badge.l}
                                    </span>
                                    <p className="mt-2.5 font-medium text-[#F4F4F4] line-clamp-2 group-hover:text-[#00d97e] transition-colors">{it.title}</p>
                                    <div className="mt-3 flex items-center gap-2">
                                        {it.artist?.avatar_url ? (
                                            <img decoding="async" src={it.artist.avatar_url} alt={artistName} className="w-6 h-6 rounded-full object-cover" loading="lazy" />
                                        ) : (
                                            <span className="w-6 h-6 rounded-full bg-[#262626] flex items-center justify-center text-[10px] font-bold text-[#00d97e]">{artistName[0]?.toUpperCase()}</span>
                                        )}
                                        <span className="text-xs text-[#A8A8A8] truncate">{artistName}</span>
                                        <span className="ml-auto text-[10px] text-[#6E6E6E] whitespace-nowrap">{timeAgo(it.created_at)}</span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
